import {
  collection,
  doc,
  getDocs,
  serverTimestamp,
  runTransaction,
  updateDoc,
  writeBatch,
  onSnapshot,
  orderBy,
  query,
  where,
  type Timestamp,
  type Unsubscribe,
  type FieldValue
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import type { User } from "firebase/auth";
import type { Asset, AssetMaintenanceRecord, AssetStatusHistoryRecord } from "../types/imobi";
import { db, storage } from "./firebase";

export type AssetRecord = Asset & {
  photoUrls: string[];
};

export type AssetStatusInput = {
  status: Asset["status"];
  reason: string;
  eventDate: string;
  expectedReturnAt?: string;
  saleValue?: number;
  resolutionNotes?: string;
  serviceProvider?: string;
  cost?: number;
  externalServiceId?: string;
  externalServiceItemId?: string;
  externalServiceTitle?: string;
  damageId?: string;
};

export type CreateAssetInput = {
  companyId: string;
  branchId: string;
  patrimonyNumber?: string;
  automaticNumbering: boolean;
  description: string;
  shortDescription: string;
  assetTypeId: string;
  physicalLocation: string;
  department?: string;
  responsible: string;
  responsibleUserId?: string;
  conservationState: Asset["conservationState"];
  serialNumber?: string;
  brand?: string;
  model?: string;
  vehiclePlate?: string;
  chassis?: string;
  renavam?: string;
  manufactureYear?: number;
  modelYear?: number;
  mileageKm?: number;
  builtAreaM2?: number;
  dimensions?: string;
  acquisitionValue?: number;
  purchaseDate?: string;
  availableForExternalService?: boolean;
  assetGroup?: string;
  customFields?: Record<string, string | number | boolean | null>;
  photos: File[];
  existingAssets: Asset[];
};

export type UpdateAssetInput = {
  patrimonyNumber?: string;
  description: string;
  shortDescription: string;
  assetTypeId: string;
  physicalLocation: string;
  department?: string;
  responsible: string;
  responsibleUserId?: string;
  conservationState: Asset["conservationState"];
  serialNumber?: string;
  brand?: string;
  model?: string;
  vehiclePlate?: string;
  chassis?: string;
  renavam?: string;
  manufactureYear?: number;
  modelYear?: number;
  mileageKm?: number;
  builtAreaM2?: number;
  dimensions?: string;
  acquisitionValue?: number;
  purchaseDate?: string;
  status?: Asset["status"];
  inactiveReason?: string;
  availableForExternalService?: boolean;
  assetGroup?: string;
  customFields?: Record<string, string | number | boolean | null>;
  canEditPatrimony: boolean;
};

export type ImportAssetInput = {
  patrimonyNumber: string;
  description: string;
  assetTypeId: string;
  physicalLocation: string;
  department?: string;
  responsible?: string;
  conservationState: Asset["conservationState"];
  status?: Asset["status"];
  serialNumber?: string;
  brand?: string;
  model?: string;
  vehiclePlate?: string;
  chassis?: string;
  renavam?: string;
  manufactureYear?: number;
  modelYear?: number;
  mileageKm?: number;
  builtAreaM2?: number;
  dimensions?: string;
  acquisitionValue?: number;
  purchaseDate?: string;
  inactiveReason?: string;
  availableForExternalService?: boolean;
  customFields?: Record<string, string | number | boolean | null>;
};

function serializeTimestamp(value: unknown): string {
  if (value && typeof value === "object" && "toDate" in value) {
    return (value as Timestamp).toDate().toISOString();
  }

  return typeof value === "string" ? value : "";
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.map(String).filter(Boolean) : [];
}

function isPhotoUrlFromAsset(url: string, companyId: string, assetId: string): boolean {
  try {
    const decodedUrl = decodeURIComponent(url);
    return decodedUrl.includes(`companies/${companyId}/assets/${assetId}/`);
  } catch {
    return false;
  }
}

function mapAsset(companyId: string, id: string, data: Record<string, unknown>): AssetRecord {
  const storedPhotoUrls = stringArray(data.photoUrls);
  const storedMainPhotoUrl = data.mainPhotoUrl ? String(data.mainPhotoUrl) : "";
  const photoUrls = storedPhotoUrls.filter((url) => isPhotoUrlFromAsset(url, companyId, id));
  const mainPhotoUrl = isPhotoUrlFromAsset(storedMainPhotoUrl, companyId, id) ? storedMainPhotoUrl : photoUrls[0];

  return {
    id,
    companyId,
    branchId: String(data.branchId ?? ""),
    patrimonyNumber: String(data.patrimonyNumber ?? ""),
    internalCode: String(data.internalCode ?? ""),
    description: String(data.description ?? ""),
    shortDescription: String(data.shortDescription ?? data.description ?? ""),
    assetTypeId: String(data.assetTypeId ?? ""),
    segment: data.segment ? String(data.segment) : undefined,
    physicalLocation: String(data.physicalLocation ?? ""),
    department: data.department ? String(data.department) : undefined,
    responsible: data.responsible ? String(data.responsible) : undefined,
    responsibleUserId: data.responsibleUserId ? String(data.responsibleUserId) : undefined,
    conservationState: (data.conservationState ?? "bom") as Asset["conservationState"],
    status: (data.status ?? "ativo") as Asset["status"],
    serialNumber: data.serialNumber ? String(data.serialNumber) : undefined,
    brand: data.brand ? String(data.brand) : undefined,
    model: data.model ? String(data.model) : undefined,
    manufacturer: data.manufacturer ? String(data.manufacturer) : undefined,
    voltage: data.voltage ? String(data.voltage) : undefined,
    power: data.power ? String(data.power) : undefined,
    color: data.color ? String(data.color) : undefined,
    dimensions: data.dimensions ? String(data.dimensions) : undefined,
    builtAreaM2: typeof data.builtAreaM2 === "number" ? data.builtAreaM2 : undefined,
    vehiclePlate: data.vehiclePlate ? String(data.vehiclePlate) : undefined,
    renavam: data.renavam ? String(data.renavam) : undefined,
    chassis: data.chassis ? String(data.chassis) : undefined,
    manufactureYear: typeof data.manufactureYear === "number" ? data.manufactureYear : undefined,
    modelYear: typeof data.modelYear === "number" ? data.modelYear : undefined,
    mileageKm: typeof data.mileageKm === "number" ? data.mileageKm : undefined,
    acquisitionValue: typeof data.acquisitionValue === "number" ? data.acquisitionValue : undefined,
    currentEstimatedValue: typeof data.currentEstimatedValue === "number" ? data.currentEstimatedValue : undefined,
    auditedValue: typeof data.auditedValue === "number" ? data.auditedValue : undefined,
    invoiceNumber: data.invoiceNumber ? String(data.invoiceNumber) : undefined,
    invoiceSeries: data.invoiceSeries ? String(data.invoiceSeries) : undefined,
    nfeAccessKey: data.nfeAccessKey ? String(data.nfeAccessKey) : undefined,
    purchaseDate: data.purchaseDate ? String(data.purchaseDate) : undefined,
    inUseDate: data.inUseDate ? String(data.inUseDate) : undefined,
    supplier: data.supplier ? String(data.supplier) : undefined,
    supplierCnpj: data.supplierCnpj ? String(data.supplierCnpj) : undefined,
    acquisitionCondition: data.acquisitionCondition as Asset["acquisitionCondition"],
    paymentMethod: data.paymentMethod ? String(data.paymentMethod) : undefined,
    costCenter: data.costCenter ? String(data.costCenter) : undefined,
    accountingAccount: data.accountingAccount ? String(data.accountingAccount) : undefined,
    estimatedUsefulLifeYears: typeof data.estimatedUsefulLifeYears === "number" ? data.estimatedUsefulLifeYears : undefined,
    annualDepreciationRate: typeof data.annualDepreciationRate === "number" ? data.annualDepreciationRate : undefined,
    depreciationMethod: data.depreciationMethod as Asset["depreciationMethod"],
    mainPhotoUrl,
    photoUrls: mainPhotoUrl && !photoUrls.includes(mainPhotoUrl) ? [mainPhotoUrl, ...photoUrls] : photoUrls,
    photosCount: typeof data.photosCount === "number" ? data.photosCount : photoUrls.length,
    labelPrintedAt: data.labelPrintedAt ? String(data.labelPrintedAt) : undefined,
    labelLostAt: data.labelLostAt ? String(data.labelLostAt) : undefined,
    auditStatus: data.auditStatus as Asset["auditStatus"],
    auditValuation: data.auditValuation as Asset["auditValuation"],
    auditorCompanyId: data.auditorCompanyId ? String(data.auditorCompanyId) : undefined,
    auditorCompanyName: data.auditorCompanyName ? String(data.auditorCompanyName) : undefined,
    auditedAt: serializeTimestamp(data.auditedAt),
    auditedBy: data.auditedBy ? String(data.auditedBy) : undefined,
    inactiveReason: data.inactiveReason ? String(data.inactiveReason) : undefined,
    statusChangedAt: serializeTimestamp(data.statusChangedAt),
    statusChangedBy: data.statusChangedBy ? String(data.statusChangedBy) : undefined,
    maintenanceOpenId: data.maintenanceOpenId ? String(data.maintenanceOpenId) : undefined,
    maintenanceStartedAt: serializeTimestamp(data.maintenanceStartedAt),
    maintenanceReason: data.maintenanceReason ? String(data.maintenanceReason) : undefined,
    availableForExternalService: data.availableForExternalService === true,
    assetGroup: data.assetGroup ? String(data.assetGroup) : undefined,
    externalServiceStatus: data.externalServiceStatus as Asset["externalServiceStatus"],
    currentExternalServiceId: data.currentExternalServiceId ? String(data.currentExternalServiceId) : undefined,
    currentExternalServiceParticipantId: data.currentExternalServiceParticipantId ? String(data.currentExternalServiceParticipantId) : undefined,
    currentExternalServiceParticipantName: data.currentExternalServiceParticipantName ? String(data.currentExternalServiceParticipantName) : undefined,
    externalServiceUpdatedAt: serializeTimestamp(data.externalServiceUpdatedAt),
    notes: data.notes ? String(data.notes) : undefined,
    customFields: typeof data.customFields === "object" && data.customFields ? data.customFields as Asset["customFields"] : {},
    createdAt: serializeTimestamp(data.createdAt),
    createdBy: String(data.createdBy ?? ""),
    updatedAt: serializeTimestamp(data.updatedAt),
    updatedBy: data.updatedBy ? String(data.updatedBy) : undefined
  };
}

function mapStatusHistory(companyId: string, assetId: string, id: string, data: Record<string, unknown>): AssetStatusHistoryRecord {
  return {
    id,
    companyId,
    assetId,
    previousStatus: (data.previousStatus ?? "ativo") as Asset["status"],
    nextStatus: (data.nextStatus ?? "ativo") as Asset["status"],
    reason: String(data.reason ?? ""),
    eventDate: serializeTimestamp(data.eventDate) || String(data.eventDate ?? ""),
    saleValue: typeof data.saleValue === "number" ? data.saleValue : undefined,
    resolutionNotes: data.resolutionNotes ? String(data.resolutionNotes) : undefined,
    externalServiceId: data.externalServiceId ? String(data.externalServiceId) : undefined,
    externalServiceItemId: data.externalServiceItemId ? String(data.externalServiceItemId) : undefined,
    externalServiceTitle: data.externalServiceTitle ? String(data.externalServiceTitle) : undefined,
    createdAt: serializeTimestamp(data.createdAt),
    createdBy: String(data.createdBy ?? ""),
    updatedAt: serializeTimestamp(data.updatedAt),
    updatedBy: data.updatedBy ? String(data.updatedBy) : undefined
  };
}

function mapMaintenanceRecord(companyId: string, assetId: string, id: string, data: Record<string, unknown>): AssetMaintenanceRecord {
  return {
    id,
    companyId,
    assetId,
    status: (data.status ?? "aberta") as AssetMaintenanceRecord["status"],
    startedAt: serializeTimestamp(data.startedAt) || String(data.startedAt ?? ""),
    reason: String(data.reason ?? ""),
    expectedReturnAt: data.expectedReturnAt ? String(data.expectedReturnAt) : undefined,
    closedAt: serializeTimestamp(data.closedAt) || (data.closedAt ? String(data.closedAt) : undefined),
    resolutionNotes: data.resolutionNotes ? String(data.resolutionNotes) : undefined,
    serviceProvider: data.serviceProvider ? String(data.serviceProvider) : undefined,
    cost: typeof data.cost === "number" ? data.cost : undefined,
    externalServiceId: data.externalServiceId ? String(data.externalServiceId) : undefined,
    externalServiceItemId: data.externalServiceItemId ? String(data.externalServiceItemId) : undefined,
    damageId: data.damageId ? String(data.damageId) : undefined,
    createdAt: serializeTimestamp(data.createdAt),
    createdBy: String(data.createdBy ?? ""),
    updatedAt: serializeTimestamp(data.updatedAt),
    updatedBy: data.updatedBy ? String(data.updatedBy) : undefined
  };
}

export function subscribeAssets(companyIds: string[], onChange: (assets: AssetRecord[]) => void, onError: (message: string) => void): Unsubscribe {
  if (!db || !companyIds.length) {
    onChange([]);
    return () => undefined;
  }

  const assetsByCompany = new Map<string, AssetRecord[]>();
  const emit = () => {
    onChange(Array.from(assetsByCompany.values()).flat().sort((left, right) => left.patrimonyNumber.localeCompare(right.patrimonyNumber)));
  };

  const unsubscribes = companyIds.map((companyId) => {
    const assetsQuery = query(collection(db!, "companies", companyId, "assets"), orderBy("patrimonyNumber"));
    return onSnapshot(
      assetsQuery,
      (snapshot) => {
        assetsByCompany.set(companyId, snapshot.docs.map((assetDoc) => mapAsset(companyId, assetDoc.id, assetDoc.data())));
        emit();
      },
      (error) => onError(error.message)
    );
  });

  return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
}

export function subscribeAssetStatusHistory(companyId: string, assetId: string, onChange: (records: AssetStatusHistoryRecord[]) => void, onError: (message: string) => void): Unsubscribe {
  if (!db) {
    onChange([]);
    return () => undefined;
  }

  return onSnapshot(
    query(collection(db, "companies", companyId, "assets", assetId, "statusHistory"), orderBy("createdAt", "desc")),
    (snapshot) => onChange(snapshot.docs.map((historyDoc) => mapStatusHistory(companyId, assetId, historyDoc.id, historyDoc.data()))),
    (error) => onError(error.message)
  );
}

export function subscribeAssetMaintenance(companyId: string, assetId: string, onChange: (records: AssetMaintenanceRecord[]) => void, onError: (message: string) => void): Unsubscribe {
  if (!db) {
    onChange([]);
    return () => undefined;
  }

  return onSnapshot(
    query(collection(db, "companies", companyId, "assets", assetId, "maintenance"), orderBy("createdAt", "desc")),
    (snapshot) => onChange(snapshot.docs.map((maintenanceDoc) => mapMaintenanceRecord(companyId, assetId, maintenanceDoc.id, maintenanceDoc.data()))),
    (error) => onError(error.message)
  );
}

function highestNumericPatrimony(assets: Asset[]) {
  return assets.reduce((highest, asset) => {
    const numeric = Number(asset.patrimonyNumber.replace(/\D/g, ""));
    return Number.isFinite(numeric) ? Math.max(highest, numeric) : highest;
  }, 0);
}

function formatPatrimonyNumber(sequence: number) {
  return String(sequence).padStart(6, "0");
}

async function uploadAssetPhotos(companyId: string, assetId: string, files: File[]) {
  if (!storage || !files.length) {
    return [];
  }

  const activeStorage = storage;
  const uploads = files.map(async (file, index) => {
    const extension = file.name.split(".").pop()?.toLowerCase() || "jpg";
    const filePath = `companies/${companyId}/assets/${assetId}/${Date.now()}-${index}.${extension}`;
    const fileRef = ref(activeStorage, filePath);
    await uploadBytes(fileRef, file, {
      contentType: file.type || "image/jpeg",
      customMetadata: {
        companyId,
        assetId
      }
    });
    return getDownloadURL(fileRef);
  });

  return Promise.all(uploads);
}

export async function createAsset(input: CreateAssetInput, user: User): Promise<{ assetId: string; patrimonyNumber: string }> {
  if (!db) {
    throw new Error("Firestore não configurado.");
  }

  const trimmedManualNumber = input.patrimonyNumber?.trim() ?? "";
  if (!input.automaticNumbering && !trimmedManualNumber) {
    throw new Error("Informe o número de patrimônio ou habilite a numeração automática.");
  }

  const assetRef = doc(collection(db, "companies", input.companyId, "assets"));
  const counterRef = doc(db, "companies", input.companyId, "counters", "assets");
  const fallbackSequence = highestNumericPatrimony(input.existingAssets.filter((asset) => asset.companyId === input.companyId));

  const patrimonyNumber = await runTransaction(db, async (transaction) => {
    const counterSnapshot = await transaction.get(counterRef);
    const currentSequence = typeof counterSnapshot.data()?.lastPatrimonyNumber === "number"
      ? Number(counterSnapshot.data()?.lastPatrimonyNumber)
      : 0;
    const nextSequence = Math.max(currentSequence, fallbackSequence) + 1;
    const generatedNumber = formatPatrimonyNumber(nextSequence);
    const finalPatrimonyNumber = input.automaticNumbering ? generatedNumber : trimmedManualNumber;

    transaction.set(assetRef, {
      branchId: input.branchId,
      patrimonyNumber: finalPatrimonyNumber,
      internalCode: finalPatrimonyNumber,
      description: input.description.trim(),
      shortDescription: input.shortDescription.trim() || input.description.trim(),
      assetTypeId: input.assetTypeId,
      physicalLocation: input.physicalLocation,
      department: input.department || null,
      responsible: input.responsible,
      responsibleUserId: input.responsibleUserId || null,
      conservationState: input.conservationState,
      status: "ativo",
      serialNumber: input.serialNumber?.trim() || null,
      brand: input.brand || null,
      model: input.model?.trim() || null,
      vehiclePlate: input.vehiclePlate?.trim() || null,
      chassis: input.chassis?.trim() || null,
      renavam: input.renavam?.trim() || null,
      manufactureYear: input.manufactureYear ?? null,
      modelYear: input.modelYear ?? null,
      mileageKm: input.mileageKm ?? null,
      builtAreaM2: input.builtAreaM2 ?? null,
      dimensions: input.dimensions?.trim() || null,
      acquisitionValue: input.acquisitionValue ?? null,
      purchaseDate: input.purchaseDate || null,
      availableForExternalService: input.availableForExternalService === true,
      assetGroup: input.availableForExternalService === true ? input.assetGroup || null : null,
      externalServiceStatus: "disponivel",
      customFields: input.customFields ?? {},
      depreciationMethod: "linear_mensal",
      auditStatus: "nao_auditado",
      photoUrls: [],
      photosCount: 0,
      createdAt: serverTimestamp(),
      createdBy: user.uid,
      updatedAt: serverTimestamp(),
      updatedBy: user.uid
    });

    transaction.set(counterRef, {
      lastPatrimonyNumber: input.automaticNumbering ? nextSequence : Math.max(currentSequence, fallbackSequence),
      updatedAt: serverTimestamp(),
      updatedBy: user.uid
    }, { merge: true });

    return finalPatrimonyNumber;
  });

  const photoUrls = await uploadAssetPhotos(input.companyId, assetRef.id, input.photos);
  if (photoUrls.length) {
    await updateDoc(assetRef, {
      photoUrls,
      mainPhotoUrl: photoUrls[0],
      photosCount: photoUrls.length,
      updatedAt: serverTimestamp(),
      updatedBy: user.uid
    });
  }

  return { assetId: assetRef.id, patrimonyNumber };
}

export async function updateAsset(companyId: string, assetId: string, input: UpdateAssetInput, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  const payload: Record<string, string | number | boolean | null | FieldValue | Record<string, string | number | boolean | null>> = {
    description: input.description.trim(),
    shortDescription: input.shortDescription.trim() || input.description.trim(),
    assetTypeId: input.assetTypeId,
    physicalLocation: input.physicalLocation,
    department: input.department || null,
    responsible: input.responsible,
    responsibleUserId: input.responsibleUserId || null,
    conservationState: input.conservationState,
    serialNumber: input.serialNumber?.trim() || null,
    brand: input.brand || null,
    model: input.model?.trim() || null,
    vehiclePlate: input.vehiclePlate?.trim() || null,
    chassis: input.chassis?.trim() || null,
    renavam: input.renavam?.trim() || null,
    manufactureYear: input.manufactureYear ?? null,
    modelYear: input.modelYear ?? null,
    mileageKm: input.mileageKm ?? null,
    builtAreaM2: input.builtAreaM2 ?? null,
    dimensions: input.dimensions?.trim() || null,
    acquisitionValue: input.acquisitionValue ?? null,
    purchaseDate: input.purchaseDate || null,
    availableForExternalService: input.availableForExternalService === true,
    assetGroup: input.availableForExternalService === true ? input.assetGroup || null : null,
    status: input.status ?? "ativo",
    inactiveReason: input.status && input.status !== "ativo" ? input.inactiveReason?.trim() || null : null,
    customFields: input.customFields ?? {},
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  };

  const patrimonyNumber = input.patrimonyNumber?.trim();
  if (input.canEditPatrimony && patrimonyNumber) {
    payload.patrimonyNumber = patrimonyNumber;
    payload.internalCode = patrimonyNumber;
  }

  await updateDoc(doc(db, "companies", companyId, "assets", assetId), payload);
}

export async function updateAssetStatus(companyId: string, asset: Asset, input: AssetStatusInput, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  const reason = input.reason.trim();
  if (!reason) {
    throw new Error("Informe o motivo da alteracao de status.");
  }

  if (input.status === "ativo" && asset.status === "em_manutencao" && !input.resolutionNotes?.trim()) {
    throw new Error("Informe o que foi feito antes de retornar o bem para operacao.");
  }

  if (input.status === "em_manutencao" && !input.externalServiceId && (asset.externalServiceStatus === "em_servico" || asset.externalServiceStatus === "em_retorno")) {
    throw new Error("Este bem está em uso em um serviço externo. Registre a devolução antes de enviar para manutenção.");
  }

  const assetRef = doc(db, "companies", companyId, "assets", asset.id);
  const historyRef = doc(collection(db, "companies", companyId, "assets", asset.id, "statusHistory"));
  const now = serverTimestamp();
  const eventDate = input.eventDate || new Date().toISOString().slice(0, 10);
  const assetPayload: Record<string, string | number | null | FieldValue> = {
    status: input.status,
    inactiveReason: input.status === "ativo" ? null : reason,
    statusChangedAt: now,
    statusChangedBy: user.uid,
    updatedAt: now,
    updatedBy: user.uid
  };

  const batch = writeBatch(db);
  batch.set(historyRef, {
    companyId,
    assetId: asset.id,
    previousStatus: asset.status,
    nextStatus: input.status,
    reason,
    eventDate,
    saleValue: input.saleValue ?? null,
    resolutionNotes: input.resolutionNotes?.trim() || null,
    externalServiceId: input.externalServiceId ?? null,
    externalServiceItemId: input.externalServiceItemId ?? null,
    externalServiceTitle: input.externalServiceTitle?.trim() || null,
    createdAt: now,
    createdBy: user.uid,
    updatedAt: now,
    updatedBy: user.uid
  });

  if (input.status === "em_manutencao") {
    const maintenanceRef = doc(collection(db, "companies", companyId, "assets", asset.id, "maintenance"));
    batch.set(maintenanceRef, {
      companyId,
      assetId: asset.id,
      status: "aberta",
      startedAt: eventDate,
      reason,
      expectedReturnAt: input.expectedReturnAt || null,
      serviceProvider: input.serviceProvider?.trim() || null,
      cost: input.cost ?? null,
      externalServiceId: input.externalServiceId ?? null,
      externalServiceItemId: input.externalServiceItemId ?? null,
      damageId: input.damageId ?? null,
      createdAt: now,
      createdBy: user.uid,
      updatedAt: now,
      updatedBy: user.uid
    });
    assetPayload.maintenanceOpenId = maintenanceRef.id;
    assetPayload.maintenanceStartedAt = now;
    assetPayload.maintenanceReason = reason;
  }

  if (asset.status === "em_manutencao" && input.status === "ativo") {
    const maintenanceQuery = query(collection(db, "companies", companyId, "assets", asset.id, "maintenance"), where("status", "==", "aberta"));
    const openMaintenances = await getDocs(maintenanceQuery);
    openMaintenances.docs.forEach((maintenanceDoc) => {
      batch.update(maintenanceDoc.ref, {
        status: "concluida",
        closedAt: eventDate,
        resolutionNotes: input.resolutionNotes?.trim() || null,
        cost: input.cost ?? null,
        serviceProvider: input.serviceProvider?.trim() || null,
        updatedAt: now,
        updatedBy: user.uid
      });
    });
    assetPayload.maintenanceOpenId = null;
    assetPayload.maintenanceStartedAt = null;
    assetPayload.maintenanceReason = null;
  }

  batch.update(assetRef, assetPayload);
  await batch.commit();
}

export async function importAssetsFromCsv(companyId: string, branchId: string, rows: ImportAssetInput[], existingAssets: Asset[], user: User): Promise<number> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  if (!rows.length) {
    return 0;
  }

  const existingNumbers = new Set(existingAssets
    .filter((asset) => asset.companyId === companyId)
    .map((asset) => asset.patrimonyNumber.trim().toLowerCase())
  );
  const importedNumbers = new Set<string>();
  rows.forEach((row) => {
    const normalized = row.patrimonyNumber.trim().toLowerCase();
    if (!normalized) {
      throw new Error("Existem linhas sem numero de patrimonio.");
    }
    if (existingNumbers.has(normalized) || importedNumbers.has(normalized)) {
      throw new Error(`Numero de patrimonio duplicado na importacao: ${row.patrimonyNumber}.`);
    }
    importedNumbers.add(normalized);
  });

  const counterRef = doc(db, "companies", companyId, "counters", "assets");
  const highestImported = rows.reduce((highest, row) => {
    const numeric = Number(row.patrimonyNumber.replace(/\D/g, ""));
    return Number.isFinite(numeric) ? Math.max(highest, numeric) : highest;
  }, 0);
  const highestExisting = highestNumericPatrimony(existingAssets.filter((asset) => asset.companyId === companyId));
  const maxSequence = Math.max(highestExisting, highestImported);

  const chunks: ImportAssetInput[][] = [];
  for (let index = 0; index < rows.length; index += 400) {
    chunks.push(rows.slice(index, index + 400));
  }

  for (const chunk of chunks) {
    const batch = writeBatch(db);
    chunk.forEach((row) => {
      const assetRef = doc(collection(db!, "companies", companyId, "assets"));
      const status = row.status ?? "ativo";
      batch.set(assetRef, {
        branchId,
        patrimonyNumber: row.patrimonyNumber.trim(),
        internalCode: row.patrimonyNumber.trim(),
        description: row.description.trim(),
        shortDescription: row.description.trim(),
        assetTypeId: row.assetTypeId,
        physicalLocation: row.physicalLocation,
        department: row.department || null,
        responsible: row.responsible || null,
        responsibleUserId: null,
        conservationState: row.conservationState,
        status,
        serialNumber: row.serialNumber?.trim() || null,
        brand: row.brand || null,
        model: row.model?.trim() || null,
        vehiclePlate: row.vehiclePlate?.trim() || null,
        chassis: row.chassis?.trim() || null,
        renavam: row.renavam?.trim() || null,
        manufactureYear: row.manufactureYear ?? null,
        modelYear: row.modelYear ?? null,
        mileageKm: row.mileageKm ?? null,
        builtAreaM2: row.builtAreaM2 ?? null,
        dimensions: row.dimensions?.trim() || null,
        acquisitionValue: row.acquisitionValue ?? null,
        purchaseDate: row.purchaseDate || null,
        availableForExternalService: row.availableForExternalService === true,
        externalServiceStatus: "disponivel",
        inactiveReason: status !== "ativo" ? row.inactiveReason?.trim() || "Importado como inativo" : null,
        customFields: row.customFields ?? {},
        depreciationMethod: "linear_mensal",
        auditStatus: "nao_auditado",
        photoUrls: [],
        photosCount: 0,
        createdAt: serverTimestamp(),
        createdBy: user.uid,
        updatedAt: serverTimestamp(),
        updatedBy: user.uid
      });
    });
    batch.set(counterRef, {
      lastPatrimonyNumber: maxSequence,
      updatedAt: serverTimestamp(),
      updatedBy: user.uid
    }, { merge: true });
    await batch.commit();
  }

  return rows.length;
}

export async function updateAssetsDepreciationRule(assets: Asset[], assetTypeId: string, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  if (!assets.length) {
    return;
  }

  const batches: Asset[][] = [];
  for (let index = 0; index < assets.length; index += 450) {
    batches.push(assets.slice(index, index + 450));
  }

  for (const batchAssets of batches) {
    const batch = writeBatch(db);
    batchAssets.forEach((asset) => {
      batch.update(doc(db!, "companies", asset.companyId, "assets", asset.id), {
        assetTypeId,
        updatedAt: serverTimestamp(),
        updatedBy: user.uid
      });
    });
    await batch.commit();
  }
}

export async function addAssetPhotos(companyId: string, assetId: string, currentPhotoUrls: string[], files: File[], user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  const remainingSlots = Math.max(0, 4 - currentPhotoUrls.length);
  const urls = await uploadAssetPhotos(companyId, assetId, files.slice(0, remainingSlots));
  const photoUrls = [...currentPhotoUrls, ...urls].slice(0, 4);

  await updateAssetPhotos(companyId, assetId, photoUrls, user);
}

export async function updateAssetPhotos(companyId: string, assetId: string, photoUrls: string[], user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  const normalizedUrls = photoUrls.filter(Boolean).slice(0, 4);
  await updateDoc(doc(db, "companies", companyId, "assets", assetId), {
    photoUrls: normalizedUrls,
    mainPhotoUrl: normalizedUrls[0] ?? null,
    photosCount: normalizedUrls.length,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  });
}

export async function markAssetsLabelIssued(companyId: string, assetIds: string[], user: User): Promise<void> {
  if (!db || !assetIds.length) {
    return;
  }

  const nowIso = new Date().toISOString();
  const batch = writeBatch(db);
  assetIds.forEach((assetId) => {
    batch.update(doc(db!, "companies", companyId, "assets", assetId), {
      labelPrintedAt: nowIso,
      labelLostAt: null,
      updatedAt: serverTimestamp(),
      updatedBy: user.uid
    });
  });
  await batch.commit();
}

export async function markAssetLabelLost(companyId: string, assetId: string, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  await updateDoc(doc(db, "companies", companyId, "assets", assetId), {
    labelLostAt: new Date().toISOString(),
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  });
}

export async function getAssetByPatrimony(patrimonyNumber: string, assets: Asset[] = []): Promise<Asset | undefined> {
  return assets.find((asset) => asset.patrimonyNumber.toLowerCase() === patrimonyNumber.toLowerCase());
}

export function buildAssetQrUrl(asset: Asset): string {
  const baseUrl = import.meta.env.VITE_IMOBI_APP_URL ?? window.location.origin;
  return `${baseUrl}/assets/${asset.id}?companyId=${asset.companyId}`;
}
